import React from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../api/firebase";
import * as XLSX from "xlsx";
import { Button } from "@mui/material";

// GASのheadersと合わせておく
const headers = [
  "ユーザID", "Email", "名前", "居住県", "北海道市区町村",
  "市区町村（日本）", "国（海外）", "州（アメリカ）",
  "目的", "性別", "申込時Email",
  "申込時ふりがな", "申込時名前", "申込時電話番号", "一斉メール受信アドレス",
  "生まれた日", "生まれた月", "生まれた年", "血液型", "作成日", "サンクスイベントを見たか",
  "ウェルカムページを見たか", "管理者", "ログインしている", "オンラインか",
  "アバターを設定したか", "居住県を設定したか", "アカウントストップ", "認証",
  "参加グループID", "2025コース選択", "セカイムラ民", "支払いPhase", "2025グループ", "メール不要","3期選択","3期グループID",
  "2026参加意思", "2026グループID", "2026グループ名",
  "payments" // ←全payments詳細
];

const ExportUsersButton = () => {
  const handleExport = async () => {
    const [usersSnapshot, groupsSnapshot] = await Promise.all([
      getDocs(collection(db, "users")),
      getDocs(collection(db, "groupActivity2026Groups")),
    ]);

    const groupNameById = {};
    // members[] を正として uid → groupId の逆引きマップを作る
    // （users側の groupActivity2026.groupId は参加意思の再設定で消えることがあるため信頼しない）
    const groupIdByUid = {};
    groupsSnapshot.docs.forEach((g) => {
      const gd = g.data() || {};
      groupNameById[g.id] = gd.name || "";
      const members = Array.isArray(gd.members) ? gd.members : [];
      members.forEach((m) => {
        const uid = m && m.uid;
        if (uid) groupIdByUid[uid] = g.id;
      });
    });

    const usersData = usersSnapshot.docs.map((doc) => {
      const data = doc.data();

      const prefecture = data.prefecture || "";
      const isOverseasUS = prefecture === "海外（アメリカ）";
      const isOverseasOther = prefecture === "海外（その他）";
      const isJapan = !isOverseasUS && !isOverseasOther && !!prefecture;

      const ga = data.groupActivity2026 || {};
      const ga2026Response = ga.response || "";
      // members[] 由来の所属を優先し、無ければ users側の逆引きコピーをフォールバックに使う
      const ga2026GroupId = groupIdByUid[doc.id] || ga.groupId || "";
      const ga2026GroupName = ga2026GroupId ? (groupNameById[ga2026GroupId] || "") : "";

      // 支払いフェーズサマリ
      const payments = Array.isArray(data.payments) ? data.payments : [];
      const paymentPhases = payments
        .filter((p) => p.paymentPhase)
        .map((p) => p.paymentPhase)
        .filter((v, i, a) => a.indexOf(v) === i)
        .join(",");

      // createdAt（ISO→日付文字列）
      let createdAt = "";
      if (data.createdAt) {
        try {
          const raw = data.createdAt;
          const rawStr = String(raw);
          const parsedDate = new Date(rawStr);
          if (!isNaN(parsedDate)) {
            createdAt = parsedDate.toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" });
          } else if (typeof raw === "object" && (raw._seconds || raw.seconds)) {
            const seconds = raw._seconds || raw.seconds;
            const dateObj = new Date(seconds * 1000);
            createdAt = dateObj.toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" });
          }
        } catch {
          createdAt = "";
        }
      }

      return {
        "ユーザID": data.uid || doc.id || "",
        "Email": data.email || "",
        "名前": data.name || "",
        "居住県": data.prefecture || "",
        "北海道市区町村": data.municipality || "",
        "市区町村（日本）": isJapan ? (data.municipality || "") : "",
        "国（海外）": isOverseasOther ? (data.country || "") : "",
        "州（アメリカ）": isOverseasUS ? (data.usState || "") : "",
        "目的": data.purpose || "",
        "性別": data.sextype || "",
        "申込時Email": data.registrationEmail || "",
        "申込時ふりがな": data.registrationFurigana || "",
        "申込時名前": data.registrationName || "",
        "申込時電話番号": data.registrationPhone || "",
        "一斉メール受信アドレス": data.broadcastReplyEmail || "",
        "生まれた日": data.birthday || "",
        "生まれた月": data.birthmonth || "",
        "生まれた年": data.birthyear || "",
        "血液型": data.bloodtype || "",
        "作成日": createdAt,
        "サンクスイベントを見たか": data.hasSeenThankYou === true ? "TRUE" : "",
        "ウェルカムページを見たか": data.hasSeenWelcome === true ? "TRUE" : "",
        "管理者": data.isAdmin === true ? "TRUE" : "",
        "ログインしている": data.isLogin === true ? "TRUE" : "",
        "オンラインか": data.isOnline === true ? "TRUE" : "",
        "アバターを設定したか": data.isRegisterAvatar === true ? "TRUE" : "",
        "居住県を設定したか": data.isRegisterPrefecture === true ? "TRUE" : "",
        "アカウントストップ": data.isStop === true ? "TRUE" : "",
        "認証": data.isVerified === true ? "TRUE" : "",
        "参加グループID": data.participatingGroup || "",
        "2025コース選択": data.purpose2025FirstHalf || "",
        "セカイムラ民": data.isSekaimura === true ? "TRUE" : "",
        "支払いPhase": paymentPhases,
        "2025グループ": data.groups2025 || "",
        "メール不要": data.emailOptOut === true ? "TRUE" : "",
        "3期選択": data.purpose2025SecondHalf || "",
        "3期グループID": data.groups2025SecondHalf || "",
        "2026参加意思": ga2026Response,
        "2026グループID": ga2026GroupId,
        "2026グループ名": ga2026GroupName,

        "payments": payments.length > 0 ? JSON.stringify(payments) : ""
      };
    });

    // シートをヘッダー順で成形
    const worksheet = XLSX.utils.json_to_sheet(usersData, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    XLSX.writeFile(workbook, "users.xlsx");
  };

  return (
    <Button variant="contained" onClick={handleExport}>
      Usersエクセルエクスポート
    </Button>
  );
};

export default ExportUsersButton;
